import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useStyles } from "../../assets/styles/Theme";
import GrnPdfHeader from "../../components/documents/GrnPdfHeader";
import GrnPdfFooter from "../../components/documents/GrnPdfFooter";
import ViewDataGridPdf from "../../components/grid/ViewDataGridPdf";
import { ViewReturnNotePdfData } from "../../components/grid/GridDataPdf";
import GrnPdfNotes from "../../components/documents/GrnPdfNotes";
import GrnPdfSupplier from "../../components/documents/GrnPdfSupplier";
import GrnPdfInfo from "../../components/documents/GrnPdfInfo";
import { FormatDate } from "../../utils/common/DateUtils";
import { useTheme, ThemeProvider } from "@mui/material/styles";
import { lightTheme } from "../../assets/styles/Theme";
import { FormatDateYYYYMM } from "../../utils/common/DateUtils";
import { GenerateNoteNumber } from "../../utils/common/SupportingFunctions";
import { CON_NOTE_DELIVERY, CON_NOTE_REJECT, CON_NOTE_RETURN } from "../../utils/sys/Constants";
import { useSelector } from "react-redux";

const cellStyleLabel = {
  border: "1px solid black",
  fontWeight: "bold",
  fontSize: 12,
  textAlign: "right",
  px: 1,
  py: 0.5,
  bgcolor: "white",
  color: "black",
};

const cellStyleValue = {
  border: "1px solid black",
  fontSize: 12,
  px: 1,
  py: 0.5,
  textAlign: "right",
  bgcolor: "white",
  color: "black",
};

function ReturnNotePdf({
  docType,
  grnHeader,
  selectedSupplier,
  grnDetailsForm,
  grnPaySummery,
  grnData,
  grnItems,
  grnCompany,
  grnReturns,
  returnedProducts,
  grnReturnList,
  grnReturnMaster,
  Returncal,
  grnreturncode,
} = {}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-LK", {
      minimumFractionDigits: 2,
    }).format(Number(val || 0));

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // ─── Helper: filter any array by grnreturncode ───────────────────────────
  const filterByReturnCode = (arr) => {
    if (!grnreturncode || !Array.isArray(arr)) return arr ?? [];
    return arr.filter(
      (item) => String(item.grnreturncode) === String(grnreturncode)
    );
  };

  // ─── Filter all data sources upfront ─────────────────────────────────────
  const filteredGrnReturns      = filterByReturnCode(grnReturns);
  const filteredGrnReturnList   = filterByReturnCode(grnReturnList);
  const filteredGrnReturnMaster = filterByReturnCode(grnReturnMaster);
  const filteredReturncal       = filterByReturnCode(Returncal);

  // ─── Latest return master ─────────────────────────────────────────────────
  const getLatestReturnMaster = () => {
    const source =
      filteredGrnReturnMaster.length > 0
        ? filteredGrnReturnMaster
        : filteredGrnReturnList;

    if (!source.length) return null;

    return [...source].sort((a, b) =>
      new Date(b.endate || 0) - new Date(a.endate || 0)
    )[0];
  };

  const latestReturnMaster = getLatestReturnMaster();

  const hasValue = (value) =>
    value !== null && value !== undefined && value !== "" && value !== "0.00";

  // ─── Resolve returnedProducts (localStorage also filtered) ───────────────
  const localReturned = (() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("returnedProducts") || "[]");
      return filterByReturnCode(parsed);
    } catch {
      return [];
    }
  })();

  returnedProducts = (Array.isArray(returnedProducts) && returnedProducts.length)
    ? filterByReturnCode(returnedProducts)
    : localReturned;

  // ─── Resolve grnReturns ───────────────────────────────────────────────────
  grnReturns = filteredGrnReturns.length
    ? filteredGrnReturns
    : (filteredGrnReturnList.length &&
        filteredGrnReturnList.some((row) => Number(row?.returnedqty || 0) > 0))
    ? filteredGrnReturnList
    : returnedProducts;

  // ─── Returncal items ──────────────────────────────────────────────────────
  const latestReturncalEntry =
    filteredReturncal.length > 0
      ? filteredReturncal[filteredReturncal.length - 1]
      : null;

  const returncalItems = Array.isArray(latestReturncalEntry?.grnReturnItems)
    ? latestReturncalEntry.grnReturnItems
    : [];

  const filteredReturncalItems = returncalItems.filter(
    (item) => Number(item?.returnedqty || 0) > 0
  );

  const fallbackReturnRecords = grnReturns.length ? grnReturns : returnedProducts;

  const resolvedReturnRecords =
    filteredReturncalItems.length > 0
      ? filteredReturncalItems
      : Array.isArray(fallbackReturnRecords)
      ? fallbackReturnRecords.filter((item) => Number(item?.returnedqty || 0) > 0)
      : [];

  // ─── Display rows: scoped to grnreturncode ────────────────────────────────
  let resolvedReturnRecordsForDisplay = resolvedReturnRecords;

  if (grnreturncode) {
    // Direct filter by the passed grnreturncode prop
    const byCode = resolvedReturnRecords.filter(
      (r) => String(r?.grnreturncode) === String(grnreturncode)
    );

    if (byCode.length > 0) {
      resolvedReturnRecordsForDisplay = byCode;
    } else if (latestReturnMaster?.grnreturncode != null && latestReturnMaster?.grnreturncode !== "") {
      // Fallback: filter by latestReturnMaster's code
      const byMasterCode = resolvedReturnRecords.filter(
        (r) => String(r?.grnreturncode) === String(latestReturnMaster.grnreturncode)
      );
      if (byMasterCode.length > 0) {
        resolvedReturnRecordsForDisplay = byMasterCode;
      } else {
        // Last resort: one row per product key
        const byKey = new Map();
        resolvedReturnRecords.forEach((r) => {
          const k = String(r?.supproductcode ?? r?.productcode ?? "");
          if (k) byKey.set(k, r);
        });
        resolvedReturnRecordsForDisplay = Array.from(byKey.values());
      }
    }
  }

  // ─── Product lookup maps ──────────────────────────────────────────────────
  const grnProductsList = Array.isArray(grnItems) && grnItems.length
    ? grnItems
    : Array.isArray(grnData)
    ? grnData
    : [];

  const productLookupByProductCode = new Map();
  const productLookupBySuppCode = new Map();

  grnProductsList.forEach((product) => {
    if (product?.productcode != null)
      productLookupByProductCode.set(String(product.productcode), product);
    if (product?.supproductcode != null)
      productLookupBySuppCode.set(String(product.supproductcode), product);
  });

  console.log("Product Lookup Maps:", resolvedReturnRecordsForDisplay);

  // ─── Build table rows ─────────────────────────────────────────────────────
  const grnItemsRows = resolvedReturnRecordsForDisplay.map((item, index) => {
    const candidateCodes = [item?.productcode, item?.supproductcode, item?.grnprcode]
      .map((c) => (c == null ? "" : String(c)))
      .filter(Boolean);

    let matchedProduct = null;
    for (const code of candidateCodes) {
      matchedProduct =
        productLookupByProductCode.get(code) ||
        productLookupBySuppCode.get(code) ||
        null;
      if (matchedProduct) break;
    }

    const productcode  = item?.productcode  || matchedProduct?.productcode  || matchedProduct?.supproductcode || "";
    const productname  = item?.productname  || matchedProduct?.productname  || matchedProduct?.itemname || matchedProduct?.itemdescription || "";
    const immeasuringunit = item?.immeasuringunit || matchedProduct?.immeasuringunit || matchedProduct?.unitmeasure || "";

    const returnedqty  = Number(item?.returnedqty || 0);
    const acceptedqty  =
      parseNumber(item?.acceptedqty) ??
      parseNumber(matchedProduct?.acceptedqty) ??
      parseNumber(matchedProduct?.receivedqty) ??
      0;

       const unitprice =
  (Number(matchedProduct?.perunittaxableamount) || 0) +
  (Number(matchedProduct?.perunitchargevalue) || 0);
    const taxrate    = parseNumber(item?.taxrate)    ?? parseNumber(matchedProduct?.taxrate)    ?? 0;
    const taxPerUnit =
      parseNumber(item?.taxperunit) ??
      parseNumber(item?.perunittaxvalue) ??
      parseNumber(matchedProduct?.taxperunit) ??
      parseNumber(matchedProduct?.perunittaxvalue) ??
      (unitprice && taxrate ? (unitprice * taxrate) / 100 : 0);

    const beforeTaxFromItem   = parseNumber(item?.totalbeforetax) ?? parseNumber(item?.totaftertaxamount);
    const calculatedBeforeTax = unitprice * returnedqty;
    const totalBeforeTax      = beforeTaxFromItem !== null ? beforeTaxFromItem : (Number.isFinite(calculatedBeforeTax) ? calculatedBeforeTax : 0);

    const taxAmountFromItem    = parseNumber(item?.taxamount);
    const calculatedTaxAmount  = taxPerUnit * returnedqty;
    const taxAmount            = taxAmountFromItem !== null ? taxAmountFromItem : (Number.isFinite(calculatedTaxAmount) ? calculatedTaxAmount : 0);

    const totalAfterTaxFromItem  = parseNumber(item?.totaftertaxamount);
    const computedTotalAfterTax  = totalBeforeTax + taxAmount;
    const totalAfterTax          = totalAfterTaxFromItem !== null ? totalAfterTaxFromItem : (Number.isFinite(computedTotalAfterTax) ? computedTotalAfterTax : 0);

    return {
      ...item,
      idno: index + 1,
      productcode,
      productname,
      immeasuringunit,
      unitprice,
      receivedqty: acceptedqty,
      returnedqty,
      taxrate,
      taxvalue: taxPerUnit,
      taxAmount,
      total_price: calculatedBeforeTax,
      totalAmount: taxAmount,
      totalAfterTax,
      reason: item?.reason || item?.descrip || "",
      remarks: item?.remarks || "",
      attachmentName: item?.dtoDOCs?.[0]?.filename || item?.dtoDOCs?.[0]?.FILENAME || "",
      id: productcode || item?.id || index,
    };
  });

  const grnItemsColumns = [
    { field: "idno",            headerName: "No" },
    { field: "productcode",     headerName: "Item Code" },
    { field: "productname",     headerName: "Item Name" },
    { field: "immeasuringunit", headerName: "UOM" },
    { field: "receivedqty",     headerName: "Total Quantity" },
    { field: "returnedqty",     headerName: "Returned Quantity" },
    { field: "unitprice",       headerName: "Unit Cost",            format: formatCurrency },
    { field: "taxrate",         headerName: "Tax Rate(%)",          format: (v) => (v ? `${v}%` : "0%") },
    { field: "taxvalue",        headerName: "Tax Amount Per Unit",  format: formatCurrency },
    { field: "taxAmount",       headerName: "Tax Amount",           format: formatCurrency },
    { field: "total_price",     headerName: "Total Price",          format: formatCurrency },
  ];

  // ─── Summary totals ───────────────────────────────────────────────────────
  const summarySet = Array.isArray(latestReturncalEntry?.grnReturnSummaries) &&
    latestReturncalEntry.grnReturnSummaries.length > 0
      ? latestReturncalEntry.grnReturnSummaries
      : [];
  const summaryEntry = summarySet.length > 0 ? summarySet[summarySet.length - 1] : null;

  const masterSummarySet = Array.isArray(latestReturnMaster?.grnReturnSummaries) &&
    latestReturnMaster.grnReturnSummaries.length > 0
      ? latestReturnMaster.grnReturnSummaries
      : [];
  const masterSummaryEntry = masterSummarySet.length > 0
    ? masterSummarySet[masterSummarySet.length - 1]
    : null;

  const apiReturnSummary = summaryEntry || masterSummaryEntry || {};

  const rowTotalBeforeTax = grnItemsRows.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0);
  const rowTotalTax       = grnItemsRows.reduce((sum, item) => sum + (Number(item.taxAmount)   || 0), 0);
  const rowGrandTotal     = rowTotalBeforeTax + rowTotalTax;

  const finalTaxableAmount =
     apiReturnSummary?.totalbeforetax != null && apiReturnSummary?.totalbeforetax !== ""
      ? Number(apiReturnSummary.totalbeforetax)
      : latestReturnMaster?.totalbeforetax != null && latestReturnMaster?.totalbeforetax !== ""
      ? Number(latestReturnMaster.totalbeforetax)
      : latestReturnMaster?.tottaxableamount != null && latestReturnMaster?.tottaxableamount !== ""
      ? Number(latestReturnMaster.tottaxableamount)
      : rowTotalBeforeTax;


  const finalTaxAmount =
    apiReturnSummary?.tottaxamount != null
      ? Number(apiReturnSummary.tottaxamount)
      : latestReturnMaster?.tottaxamount != null && latestReturnMaster?.tottaxamount !== ""
      ? Number(latestReturnMaster.tottaxamount)
      : rowTotalTax;

  const grandtotal =
    latestReturnMaster?.totaftertaxamount != null && latestReturnMaster?.totaftertaxamount !== ""
      ? Number(latestReturnMaster.totaftertaxamount)
      : apiReturnSummary?.totaftertaxamount != null
      ? Number(apiReturnSummary.totaftertaxamount)
      : apiReturnSummary?.totreturnedvalue != null
      ? Number(apiReturnSummary.totreturnedvalue)
      : rowGrandTotal;

  // ─── Header / info fields ─────────────────────────────────────────────────
  const poData = grnHeader || grnDetailsForm || {};

  const poNumber =
    poData.ponumber ||  "-";
  const poDate            = poData.podate        || poData.poDate        || null;
  const invoiceNumber     = poData.invoiceno     || poData.invoiceNumber || "N/A";
  const invoiceDate       = poData.invoicedate   || poData.invoiceDate   || null;
  const deliveryNoteNumber = poData.deliverynoteno || poData.deliveryNoteNumber || "N/A";
  const deliveryDate      = poData.deliverynotedate || null;

  const companyName = grnCompany?.compname       || "N/A";
  const address1    = grnCompany?.compaddresS1   || "";
  const address2    = grnCompany?.compaddresS2   || "";
  const address3    = grnCompany?.compaddresS3   || "";
  const city        = grnCompany?.city           || "";
  const province    = grnCompany?.province       || "";
  const country     = grnCompany?.country        || "";
  const fullAddress = [address1, address2, address3, city, province, country].filter(Boolean).join(", ");
  const contact     = grnCompany?.hotline        || "N/A";
  const email       = grnCompany?.email          || "N/A";

  return (
    <div ref={printRef} id="print-section">
      <ThemeProvider theme={lightTheme}>
        <Box sx={{ maxWidth: 794, mx: "auto", p: 3, bgcolor: "white" }}>
          <GrnPdfHeader
            docType={docType}
            grnHeader={grnHeader}
            grnCompany={grnCompany}
            handlePrint={handlePrint}
          />

          <Divider sx={{ borderColor: "#2a6db0", mb: 4 }} />

          <Box mb={4} sx={{ color: "black" }}>
            <Typography variant="body_xs" component="span" fontWeight="bold">RETURN NOTE NO:</Typography>{" "}
            <Typography variant="body_xs" component="span">
              {GenerateNoteNumber(CON_NOTE_RETURN, FormatDateYYYYMM(invoiceDate),grnreturncode)}
            </Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">RETURN NOTE DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(invoiceDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">INVOICE NUMBER:</Typography>{" "}
            <Typography variant="body_xs" component="span">{invoiceNumber}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">INVOICE DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(invoiceDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">GRN ID:</Typography>{" "}
            <Typography variant="body_xs" component="span">{grnHeader?.grncode || "N/A"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">GRN DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(poDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">PO NUMBER:</Typography>{" "}
            <Typography variant="body_xs" component="span">{poNumber}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">PO DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(poDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">DELIVERY NOTE NUMBER:</Typography>{" "}
            <Typography variant="body_xs" component="span">
             {poData?.deliverynoteno ||  "-"}
            </Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">DELIVERY DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(deliveryDate) || "-"}</Typography>
          </Box>

          <Box sx={styles.sup_buy_container}>
            <Box sx={styles.sup_buy_container_inside} mt={2}>
              <Typography variant="subtitle_s" fontWeight="bold" mb={1} sx={{ textTransform: "uppercase" }}>
                BUYER INFORMATION
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small" sx={{ "& td, & th": { borderBottom: "none" } }}>
                  <TableBody>
                    <TableRow>
                      <TableCell><Typography variant="body_xs" fontWeight="bold">COMPANY NAME</Typography></TableCell>
                      <TableCell><Typography variant="body_xs">{companyName || "N/A"}</Typography></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="body_xs" fontWeight="bold">ADDRESS</Typography></TableCell>
                      <TableCell><Typography variant="body_xs">{fullAddress || "N/A"}</Typography></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="body_xs" fontWeight="bold">CONTACT / PHONE</Typography></TableCell>
                      <TableCell><Typography variant="body_xs">{contact || "N/A"}</Typography></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="body_xs" fontWeight="bold">EMAIL</Typography></TableCell>
                      <TableCell><Typography variant="body_xs">{email || "N/A"}</Typography></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={styles.sup_buy_container_inside}>
              <GrnPdfSupplier grnHeader={grnHeader} selectedSupplier={selectedSupplier} />
            </Box>
          </Box>

          <ViewDataGridPdf
            columns={grnItemsColumns}
            rows={grnItemsRows}
            gridid="productcode"
            sx={styles.view_data_grid_page}
          />

          <Box display="flex" justifyContent="flex-end" mt={2} maxWidth={300} ml="auto">
            <TableContainer component={Paper} sx={{ border: "1px solid black" }}>
              <Table size="small" sx={{ borderCollapse: "collapse" }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={cellStyleLabel}>TOTAL BEFORE TAX</TableCell>
                    <TableCell sx={cellStyleValue}>{formatCurrency(finalTaxableAmount)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={cellStyleLabel}>TOTAL TAX</TableCell>
                    <TableCell sx={cellStyleValue}>{formatCurrency(finalTaxAmount)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={cellStyleLabel}>TOTAL</TableCell>
                    <TableCell sx={cellStyleValue}>{formatCurrency(grandtotal)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <GrnPdfInfo grnHeader={grnHeader} />
          <GrnPdfFooter />
        </Box>
      </ThemeProvider>
    </div>
  );
}

export default ReturnNotePdf;